// CLARTEXT · UI-V2 · Kaufflow-Hook der Abschlussseite v2.
//
// STRICT SCOPE: verkabelt die 4 CTAs mit bereits existierender Infrastruktur.
//   1. Preis + product_id + Metadaten → GET /api/catalog (unverändert)
//   2. Firmenname → FirmennameDialog (übernommen aus EinmalkaufPanel)
//   3. Einmaliger Download → POST /billing/one-time-checkout (unverändert)
//   4. Stripe-Rückkehr → /einmalkauf?checkout=success|cancel (unverändert)
//   5. Trial („CLARTEXT übernimmt") → POST /public/demo-start-wizard
//      (dasselbe Aufruf-Muster wie Popup-Tür 2, ZweiTuerenPopup.jsx) —
//      inkl. optionalem Tätigkeitsbegriff für die KI-Welt-Generierung.
//
// Es entstehen KEINE neuen APIs, keine neuen Datenmodelle, keine neue
// Businesslogik. Fehlermeldungen und Loading-Semantik sind 1:1 aus
// EinmalkaufPanel.jsx bzw. ZweiTuerenPopup.jsx übernommen.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, api } from "@/lib/api";
import { mitHerkunft } from "@/lib/herkunft"; // MESS-07, Betreiberfreigabe 27.10.2026
import { meldeStufe } from "@/lib/messStufe"; // MESS-10c, Betreiberfreigabe 03.11.2026

// sessionStorage-Schlüssel für das "Manifest" (die read-only Weltvorschau).
// Wird vom bereits vorhandenen LandingPage-/Vorschau-Flow gespeist (heute
// noch nicht — spätere Anbindung durch die LandingPage außerhalb von UI-V2).
// In UI-V2 lesen wir den Wert defensiv: nicht vorhanden → CTA zeigt sanft
// den Grund (kein API-Call).
export const ABSCHLUSS_CONTENT_KEY = "clartext_abschluss_content";

// Roher Tätigkeitsbegriff der Vorschau-Welt (gesetzt beim Klick auf
// „Dokumentation freigeben" in Welt.jsx). Fallback: content.title.
export const ABSCHLUSS_TOPIC_KEY = "clartext_abschluss_topic";

// Clientseitige 24h-Browser-Sperre — identisch zu ZweiTuerenPopup.jsx /
// LandingPage.jsx (1 Demo pro Browser / 24 h; Backend-Limit greift zusätzlich).
const DEMO_LAST_KEY = "clartext_lp_demo_at";
const DAY_MS = 24 * 60 * 60 * 1000;

const formatEuro = (cents) =>
  (cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
export { formatEuro };

function readSessionContent() {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(ABSCHLUSS_CONTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

// Der zentrale Hook: liefert (a) Katalog-Preistext, (b) Firmenname-State,
// (c) Kauf-Handler, (d) Trial-Handler, (e) Loading/Error-State.
export function useAbschlussKauffluss() {
  const [priceCents, setPriceCents] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  const [productId, setProductId] = useState(null);
  const [content, setContent] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [companyChoice, setCompanyChoice] = useState(null); // null | "ja" | "nein"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [choicePromptTick, setChoicePromptTick] = useState(0);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState(null);
  // Doppelklick-Schutz (Ref statt State: sofort wirksam, keine Re-Render-Abhängigkeit).
  const trialBusyRef = useRef(false);

  // (1) Katalog laden — analog EinmalkaufPanel.
  useEffect(() => {
    let alive = true;
    api
      .getPublicCatalog()
      .then((data) => {
        if (!alive || !data || !Array.isArray(data.products)) return;
        const oneTime = data.products.find(
          (p) => p.pricing && p.pricing.product_type === "one_time",
        );
        if (oneTime) {
          setProductId(oneTime.product_id || null);
          if (oneTime.pricing.one_time_net_cents != null) {
            setPriceCents(oneTime.pricing.one_time_net_cents);
            setCurrency(oneTime.pricing.currency || "EUR");
          }
        }
      })
      .catch(() => {
        // Preisanzeige optional — Stripe zeigt den verbindlichen Betrag.
      });
    return () => {
      alive = false;
    };
  }, []);

  // (2) Content aus sessionStorage lesen (falls über Vorschau eingestiegen).
  useEffect(() => {
    setContent(readSessionContent());
  }, []);

  // MESS-10c, Betreiberfreigabe 03.11.2026: „Abschlussseite gesehen“ nur mit gültigem Inhalt, je Welt und Tab einmal.
  useEffect(() => {
    if (content) meldeStufe("abschluss_gesehen", content.world_id);
  }, [content]);

  // MESS-10c, Betreiberfreigabe 03.11.2026: „Abo-Knopf getippt“, je Welt und Tab einmal.
  const meldeAboKnopf = useCallback(() => {
    meldeStufe("knopf_abo", content ? content.world_id : null);
  }, [content]);

  // Textzeile: „9,90 € netto, einmalig zzgl. MwSt." (identische Formatierung
  // wie EinmalkaufPanel; sichtbarer Preis kommt aus /api/catalog).
  const priceText = useMemo(() => {
    if (priceCents == null) return null;
    const cur = currency === "EUR" ? "€" : currency;
    return `${formatEuro(priceCents)} ${cur} netto, einmalig zzgl. MwSt.`;
  }, [priceCents, currency]);

  // Buy-Handler — identische Fehlerklassen wie EinmalkaufPanel.
  const buy = useCallback(async () => {
    setError(null);
    if (companyChoice === null) {
      setError("Bitte wählen Sie zuerst, ob Ihr Firmenname auf dem PDF erscheinen soll.");
      setChoicePromptTick((t) => t + 1);
      return;
    }
    if (!productId) {
      setError("Der Einmalkauf ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.");
      return;
    }
    if (!content) {
      setError(
        "Für den Kauf wird die zuvor freigegebene Vorschau benötigt. " +
          "Bitte starten Sie zuerst über die Startseite und geben Sie Ihre Welt frei.",
      );
      return;
    }
    setLoading(true);
    try {
      const res = await api.createOneTimeCheckout({
        origin_url: window.location.origin,
        content,
        company_name: companyName.trim(),
        product_id: productId,
      });
      if (res && res.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("no_url");
    } catch (e) {
      setLoading(false);
      const detail = e?.response?.data?.detail;
      if (detail === "checkout_disabled") {
        setError(
          "Der Einmalkauf ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.",
        );
      } else if (detail === "rate_limited") {
        setError(
          "Zu viele Anfragen. Bitte versuchen Sie es in einer Stunde erneut.",
        );
      } else {
        setError(
          "Der Kauf konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
        );
      }
    }
  }, [companyChoice, productId, content, companyName]);

  // Trial-Handler: „CLARTEXT übernimmt" → bestehender Self-Service-Endpunkt
  // POST /public/demo-start-wizard — exakter Kern des startDemo-Flows aus
  // ZweiTuerenPopup.jsx (Z. 125–192): 24h-Browser-Sperre, credentials:include,
  // Einmal-Zugangsdaten via localStorage/pending_demo_creds, Redirect
  // /unternehmer. NEU (betreiberfreigegeben): der Tätigkeitsbegriff der
  // Vorschau-Welt wird als optionales `topic` mitgegeben, damit der frische
  // Demo-Mandant direkt eine passende Welt generiert.
  const startTrial = useCallback(async () => {
    if (trialBusyRef.current) return;
    try {
      const last = Number(localStorage.getItem(DEMO_LAST_KEY) || 0);
      if (last && Date.now() - last < DAY_MS) {
        setTrialError(
          "Sie haben kürzlich bereits einen Testzugang gestartet. Bitte versuchen Sie es später erneut.",
        );
        return;
      }
    } catch {
      /* localStorage nicht verfügbar — Backend-Limit greift trotzdem */
    }
    trialBusyRef.current = true;
    setTrialLoading(true);
    setTrialError(null);
    let topic = "";
    try {
      topic = (window.sessionStorage.getItem(ABSCHLUSS_TOPIC_KEY) || "").trim();
    } catch {
      /* sessionStorage optional */
    }
    if (!topic && content && typeof content.title === "string") {
      topic = content.title.trim();
    }
    try {
      const res = await fetch(`${API}/public/demo-start-wizard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(mitHerkunft(topic ? { topic } : {})),
      });
      if (!res.ok) {
        trialBusyRef.current = false;
        setTrialLoading(false);
        setTrialError(
          res.status === 429
            ? "Zu viele Testzugänge von dieser Verbindung. Bitte versuchen Sie es morgen erneut."
            : "Der Testzugang konnte nicht gestartet werden. Bitte versuchen Sie es später erneut.",
        );
        return;
      }
      const data = await res.json();
      try {
        localStorage.setItem(
          "owner_session",
          JSON.stringify({
            tenant_id: data.tenant_id,
            company_name: data.company_name,
            company_code: data.company_code,
            must_change_pin: !!data.must_change_pin,
          }),
        );
        if (data.owner_pin) {
          localStorage.setItem(
            "pending_demo_creds",
            JSON.stringify({
              demo_tenant_id: data.tenant_id,
              company_code: data.company_code,
              owner_pin: data.owner_pin,
            }),
          );
        }
        localStorage.setItem(DEMO_LAST_KEY, String(Date.now()));
      } catch {
        /* Session unkritisch persistierbar — Redirect erfolgt trotzdem */
      }
      window.location.href = "/unternehmer";
    } catch {
      trialBusyRef.current = false;
      setTrialLoading(false);
      setTrialError("Verbindungsproblem. Bitte versuchen Sie es später erneut.");
    }
  }, [content]);

  return {
    priceCents,
    priceText,
    productId,
    companyName,
    setCompanyName,
    companyChoice,
    setCompanyChoice,
    choicePromptTick,
    hasContent: content !== null,
    loading,
    error,
    buy,
    startTrial,
    trialLoading,
    trialError,
    meldeAboKnopf, // MESS-10c, Betreiberfreigabe 03.11.2026
  };
}
